import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { MiqSecuredResourcesService, TRANSLATOR, Translator } from '@miq/utils';
import { saveAs } from 'file-saver';
import { FloorClass } from '../../../models/floor.class';
import { AbstractSpaceClass } from '../../../models/space/abstract-space.class';
import { UnaccountedSpaceClass } from '../../../models/space/unaccounted-space.class';
import { SHOW_FLOOR_INDEX_FEATURE_KEY } from '../../../shared/constants/features.const';
import { StackingPlanService } from '../../../stacking-plan.service';
import { genThumbnailUrl } from '../../../thumbnail.const';
import { MediaInfo } from '../../stacking-plan.model';
import { AreaUrl } from './item/details-overlay/details-overlay.constants';
import { DataObject } from '../../../models/data-object.models';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';
import { FEATURE_TOGGLER, FeatureToggler } from '@cbre/feature-toggles';

@Component({
  selector: 'sp-floor',
  templateUrl: './floor.component.html',
  styleUrls: ['./floor.component.scss'],
  standalone: false,
})
export class FloorComponent implements OnInit, OnDestroy {
  @Input() floorData: FloorClass;
  @Input() totalNumberOfFloors: number;
  @Input() containerRecordFloors: DataObject[];
  @Input() selectedUsageType: string = null;
  @Input() highlightVirtualFloor: boolean;

  public floorLabel: string;
  public floorAreaLabel: string;
  public isFloorIndexValid = true;
  public isFloorAreaExceeded = false;
  public items: AbstractSpaceClass[] = [];
  public showInformationNotAvailable: boolean;

  constructor(
    @Inject(FEATURE_TOGGLER) private toggleRouterService: FeatureToggler,
    private securedResourcesService: MiqSecuredResourcesService,
    private stackingPlanService: StackingPlanService,
    @Inject(TRANSLATOR) private translationService: Translator,
    @Inject(CbreLoggerToken) private readonly logger: CbreLogger
  ) {}

  ngOnInit() {
    if (!!this.floorData) {
      this.getItemData();
    }
  }

  ngOnDestroy(): void {
    /* required by angular-l10n */
  }

  getItemData() {
    this.isFloorIndexValid = this.floorData.isIndexValid();
    this.floorLabel = this.floorData.generateFloorLabel(
      this.toggleRouterService.isFeatureEnabled(SHOW_FLOOR_INDEX_FEATURE_KEY)
    );
    this.floorAreaLabel = this.floorData.generateFloorSpace();
    if (this.floorAreaLabel) {
      this.stackingPlanService.getLovValuesMap(AreaUrl).subscribe(mappedUomLov => {
        this.floorAreaLabel = this.floorData.generateFloorSpace(mappedUomLov);
      });
    }
    if (!!this.floorData.spaces) {
      this.items = [...this.floorData.spaces];
      this.isFloorAreaExceeded = this.floorData.isFloorAreaExceeded();
      this.setShowInformationNotAvailable();
      this.addUnaccountedSpace();
    }
  }

  downloadFloorPlan(floorImageURL: string) {
    const mediaOptions: MediaInfo = this.stackingPlanService.media;
    const url: string = genThumbnailUrl(floorImageURL, mediaOptions);
    this.securedResourcesService.downloadImage(url).subscribe(
      ({ blob }) => {
        const extension = floorImageURL.split('.');
        if (extension[extension.length - 1] === 'pdf') {
          const blobURL = URL.createObjectURL(blob);
          window.open(blobURL);
        } else {
          saveAs(blob, this.floorData.generateFloorPlanFileName());
        }
      },
      error => {
        this.logger.error(`Error downloading floor plan: ${error}`);
      }
    );
  }

  private setShowInformationNotAvailable() {
    this.showInformationNotAvailable = !this.floorData.isFloorSpaceEqualOrExceeded();
  }

  private addUnaccountedSpace() {
    const unaccountedSpace = this.floorData.getUnaccountedSpace();
    if (!!unaccountedSpace) {
      this.items.push(
        new UnaccountedSpaceClass(
          unaccountedSpace,
          this.floorData.floorAreaUOM,
          this.translationService,
          this.floorData.isHighlighted
        )
      );
      this.showInformationNotAvailable = false;
    }
  }
}
